<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminNavbar active="coupons" />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
                <div>
                    <h1 class="text-base sm:text-xl font-bold text-gray-800">Cupons Stripe</h1>
                    <p class="text-sm text-gray-400 mt-1">Criar e gerenciar cupons e promotion codes.</p>
                </div>
                <div class="flex flex-wrap gap-2">
                    <a :href="stripeCouponsUrl" target="_blank" rel="noopener noreferrer" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight">
                        Ver no Stripe
                    </a>
                    <router-link :to="{ name: 'adminCouponNew', params: { uuid } }" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight">
                        Novo cupom
                    </router-link>
                </div>
            </div>

            <div v-if="error" class="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {{ error }}
            </div>

            <div v-if="loading" class="space-y-3">
                <div v-for="i in 4" :key="i" class="skeleton h-24 w-full rounded-xl"></div>
            </div>

            <template v-else>
                <div v-if="coupons.length === 0" class="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center">
                    <div class="text-sm font-semibold text-gray-700">Nenhum cupom encontrado</div>
                    <p class="mt-2 text-sm text-gray-500">Crie um cupom para gerar discount + promotion code inicial no Stripe.</p>
                    <router-link :to="{ name: 'adminCouponNew', params: { uuid } }" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight mt-4">
                        Criar cupom
                    </router-link>
                </div>

                <div v-else class="hidden sm:block overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                    <table class="table table-sm w-full min-w-[980px]">
                        <thead>
                            <tr class="text-[11px] uppercase tracking-wide">
                                <th class="text-left !text-black">Cupom</th>
                                <th class="text-left !text-black">Desconto</th>
                                <th class="text-left !text-black">Duração</th>
                                <th class="text-center !text-black">Status</th>
                                <th class="text-left !text-black">Validade</th>
                                <th class="text-center !text-black">Usos</th>
                                <th class="text-right !text-black">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="coupon in coupons"
                                :key="coupon.id"
                                class="hover:bg-gray-50 transition-colors cursor-pointer"
                                @click="openCouponDetails(coupon.id)"
                            >
                                <td class="whitespace-nowrap">
                                    <div class="font-medium text-gray-800">{{ coupon.name }}</div>
                                    <div class="text-xs font-mono text-gray-400">{{ coupon.id }}</div>
                                </td>
                                <td class="text-gray-600">
                                    <div>{{ formatDiscount(coupon) }}</div>
                                    <div class="text-xs text-gray-400">{{ formatCouponType(coupon) }}</div>
                                </td>
                                <td class="text-gray-600 whitespace-nowrap">{{ formatDuration(coupon) }}</td>
                                <td class="text-center">
                                    <span :class="adminBadgeClass(coupon.valid ? 'success' : 'error')">{{ coupon.valid ? 'Ativo' : 'Inativo' }}</span>
                                </td>
                                <td class="whitespace-nowrap">
                                    <div class="text-gray-600">{{ formatValidity(coupon.redeem_by) }}</div>
                                    <div class="text-xs text-gray-400">{{ coupon.created_at ? `Criado ${formatRelative(coupon.created_at)}` : '—' }}</div>
                                </td>
                                <td class="text-center text-gray-600">{{ coupon.times_redeemed }}<span v-if="coupon.max_redemptions"> / {{ coupon.max_redemptions }}</span></td>
                                <td class="text-right whitespace-nowrap">
                                    <router-link :to="{ name: 'adminCouponDetail', params: { uuid, couponId: coupon.id } }" class="text-xs text-cyan-600 hover:text-cyan-700 transition-colors mr-3" @click.stop>
                                        Detalhes
                                    </router-link>
                                    <a :href="buildStripeCouponUrl(coupon.id)" target="_blank" rel="noopener noreferrer" class="text-xs text-gray-500 hover:text-gray-800 transition-colors mr-3" @click.stop>
                                        Stripe
                                    </a>
                                    <button type="button" class="text-xs text-red-500 hover:text-red-700 transition-colors" :disabled="deletingCouponId === coupon.id" @click.stop="handleDeleteCoupon(coupon)">
                                        {{ deletingCouponId === coupon.id ? 'Apagando...' : 'Apagar' }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="coupons.length > 0" class="sm:hidden space-y-3">
                    <div
                        v-for="coupon in coupons"
                        :key="coupon.id"
                        class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 min-w-0 overflow-hidden active:bg-gray-50"
                        @click="openCouponDetails(coupon.id)"
                    >
                        <div class="flex flex-wrap items-center gap-2 mb-3">
                            <span :class="adminBadgeClass(coupon.valid ? 'success' : 'error')">{{ coupon.valid ? 'Ativo' : 'Inativo' }}</span>
                            <span :class="adminBadgeClass('neutral')">{{ formatDuration(coupon) }}</span>
                        </div>
                        <div class="min-w-0 mb-4">
                            <div class="text-2xl font-semibold leading-tight text-gray-800">{{ coupon.name }}</div>
                            <div class="text-xs font-mono text-gray-400 break-all mt-1">{{ coupon.id }}</div>
                        </div>
                        <div class="grid grid-cols-2 gap-3 mb-4">
                            <div class="rounded-lg bg-gray-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Desconto</div>
                                <div class="text-sm font-semibold text-gray-700 mt-1">{{ formatDiscount(coupon) }}</div>
                                <div class="text-xs text-gray-400 mt-1">{{ formatCouponType(coupon) }}</div>
                            </div>
                            <div class="rounded-lg bg-gray-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Validade</div>
                                <div class="text-sm font-semibold text-gray-700 mt-1">{{ formatValidity(coupon.redeem_by) }}</div>
                            </div>
                            <div class="col-span-2 rounded-lg bg-gray-50 px-3 py-2">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Usos</div>
                                <div class="text-sm font-semibold text-gray-700 mt-1">{{ coupon.times_redeemed }}<span v-if="coupon.max_redemptions"> / {{ coupon.max_redemptions }}</span></div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                            <div class="min-w-0">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Criado</div>
                                <div class="text-sm text-gray-600 mt-1">{{ coupon.created_at ? formatRelative(coupon.created_at) : '—' }}</div>
                            </div>
                            <div class="min-w-0">
                                <div class="text-[11px] uppercase tracking-wide text-gray-400">Ações</div>
                                <div class="flex flex-wrap gap-3 mt-1">
                                    <router-link :to="{ name: 'adminCouponDetail', params: { uuid, couponId: coupon.id } }" class="text-xs text-cyan-600 hover:text-cyan-700 transition-colors" @click.stop>
                                        Detalhes
                                    </router-link>
                                    <a :href="buildStripeCouponUrl(coupon.id)" target="_blank" rel="noopener noreferrer" class="text-xs text-gray-500 hover:text-gray-800 transition-colors" @click.stop>
                                        Stripe
                                    </a>
                                    <button type="button" class="text-xs text-red-500 hover:text-red-700 transition-colors" :disabled="deletingCouponId === coupon.id" @click.stop="handleDeleteCoupon(coupon)">
                                        {{ deletingCouponId === coupon.id ? 'Apagando...' : 'Apagar' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import AdminNavbar from '@/components/admin/AdminNavbar.vue';
import { adminBadgeClass } from '@/constants/adminBadge';
import AdminStripeService from '@/services/admin/AdminStripeService';

const STRIPE_COUPONS_URL = 'https://dashboard.stripe.com/coupons';

export default {
    name: 'AdminCoupons',
    components: { AdminNavbar },
    setup() {
        const toast = useToast();
        const route = useRoute();
        const router = useRouter();
        const uuid = computed(() => route.params.uuid);
        const coupons = ref([]);
        const loading = ref(true);
        const error = ref('');
        const deletingCouponId = ref('');

        async function loadCoupons() {
            loading.value = true;
            error.value = '';
            try {
                const response = await AdminStripeService.listCoupons();
                coupons.value = response?.data?.data || [];
            } catch (err) {
                error.value = err.response?.data?.text || 'Erro ao carregar cupons';
                coupons.value = [];
            } finally {
                loading.value = false;
            }
        }

        async function handleDeleteCoupon(coupon) {
            if (!coupon?.id || deletingCouponId.value) return;

            const confirmed = window.confirm(`Apagar cupom ${coupon.name}? Isso desativa os promotion codes dele no Stripe.`);
            if (!confirmed) return;

            deletingCouponId.value = coupon.id;
            try {
                await AdminStripeService.deleteCoupon(coupon.id);
                coupons.value = coupons.value.filter(item => item.id !== coupon.id);
                toast.open({ message: 'Cupom apagado com sucesso.', type: 'success', position: 'top-right' });
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível apagar o cupom.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                deletingCouponId.value = '';
            }
        }

        function buildStripeCouponUrl(couponId) {
            return `${STRIPE_COUPONS_URL}/${couponId}`;
        }

        function openCouponDetails(couponId) {
            if (!couponId) return;
            router.push({ name: 'adminCouponDetail', params: { uuid: uuid.value, couponId } });
        }

        function formatDiscount(coupon) {
            if (coupon.percent_off) return `${coupon.percent_off}% off`;
            if (coupon.amount_off) return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(coupon.amount_off);
            return '—';
        }

        function formatCouponType(coupon) {
            if (coupon.percent_off) return 'Percentual';
            if (coupon.amount_off) return 'Valor fixo';
            return 'Sem desconto';
        }

        function formatDuration(coupon) {
            if (coupon.duration === 'once') return 'Uma vez';
            if (coupon.duration === 'repeating') return coupon.duration_in_months ? `${coupon.duration_in_months} mês(es)` : 'Vários meses';
            if (coupon.duration === 'forever') return 'Para sempre';
            return '—';
        }

        function formatValidity(value) {
            if (!value) return 'Sem limite';
            return new Date(value).toLocaleDateString('pt-BR');
        }

        function formatRelative(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '—';

            const diffMs = Date.now() - date.getTime();
            const diffMin = Math.floor(diffMs / 60000);
            const diffHour = Math.floor(diffMs / 3600000);
            const diffDay = Math.floor(diffMs / 86400000);
            const diffMonth = Math.floor(diffDay / 30);
            const diffYear = Math.floor(diffDay / 365);

            if (diffMin < 1) return 'agora';
            if (diffMin < 60) return `há ${diffMin} min`;
            if (diffHour < 24) return `há ${diffHour} h`;
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        onMounted(() => {
            loadCoupons();
        });

        return {
            uuid,
            coupons,
            loading,
            error,
            deletingCouponId,
            handleDeleteCoupon,
            buildStripeCouponUrl,
            openCouponDetails,
            formatDiscount,
            formatCouponType,
            formatDuration,
            formatValidity,
            formatRelative,
            adminBadgeClass,
            stripeCouponsUrl: STRIPE_COUPONS_URL
        };
    }
};
</script>
